<ion-header translucent="true" collapse="fade" class="ion-no-border">
  <ion-toolbar class="bg-gradient-to-r from-primary/5 to-transparent">
    <ion-buttons slot="start">
      <ion-menu-button class="text-primary hover:bg-primary/10 rounded-full transition-all duration-300" mode="md" />
    </ion-buttons>
    <ion-title class="font-bold tracking-tight">{{ "COMMON.HOME" | translate }}</ion-title>
    @if (pwaInstallStore.isInstallable()) {
      <ion-buttons slot="end" class="md:me-3">
        <mtjr-pwa-install-button />
      </ion-buttons>
    }
  </ion-toolbar>
  <ion-toolbar class="items-center px-2 pb-2" mode="ios">
    <div class="relative w-full md:max-w-[550px] mx-auto">
      <ion-searchbar
        mode="ios"
        class="custom-searchbar w-full shadow-lg shadow-primary/10 rounded-2xl overflow-hidden"
        [placeholder]="'COMMON.SEARCH' | translate"
        #searchbar
        (keyup.enter)="handleSearchInput(searchbar.value)"
      />
    </div>
  </ion-toolbar>
</ion-header>

<ion-content fullscreen="true" class="bg-gradient-to-b from-gray-50/50 to-white dark:from-gray-900/50 dark:to-gray-950">
  @if (store.anyLoading()) {
    <div class="flex items-center justify-center min-h-[60vh]">
      <mtjr-loading />
    </div>
  } @else if (store.categoriesFailure() || store.landingPageFailure()) {
    <mtjr-error [failure]="store.categoriesFailure() || store.landingPageFailure()" />
  } @else if (store.mergedData()) {
    <div class="pb-10 space-y-6">
      @for (section of store.sections(); track $index) {
        <section class="animate-fade-in" [style.animation-delay]="$index * 100 + 'ms'">
          @if (section.title) {
            <div class="px-4 md:px-6 pt-2 pb-2 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-1 h-6 bg-gradient-to-b from-primary to-primary/50 rounded-full"></div>
                <h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">{{ section.title }}</h2>
              </div>
              @if (section.viewAllFilter) {
                <ion-button
                  fill="clear"
                  size="small"
                  class="text-primary font-semibold hover:bg-primary/10 rounded-xl transition-all duration-300"
                  (click)="navigateToSection(section)"
                >
                  {{ "COMMON.VIEW_ALL" | translate }}
                  <ion-icon name="chevron-forward-outline" slot="end" class="text-sm"></ion-icon>
                </ion-button>
              }
            </div>
          }
          @switch (section.layout) {
            @case ("slides") {
              <div class="px-4 md:px-6">
                <swiper-container
                  class="md:hidden rounded-2xl overflow-hidden shadow-xl shadow-black/10"
                  style="--swiper-scrollbar-display: none"
                  autoplay-delay="3000"
                  autoplay-disable-on-interaction="false"
                  loop="true"
                >
                  @for (slide of section.slides; track $index) {
                    <swiper-slide>
                      <div class="relative group">
                        <mtjr-blur-img
                          [src]="slide.image"
                          alt="{{ 'PRODUCTS.PRODUCT_IMAGE' | translate }}"
                          containerClass="h-[220px] w-full"
                          imgClass="h-full w-full !rounded-2xl object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div
                          class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl pointer-events-none"
                        ></div>
                      </div>
                    </swiper-slide>
                  }
                  <slides-preview-preview></slides-preview-preview>
                </swiper-container>
                <div
                  class="hidden gap-4 overflow-x-auto py-2 lg:flex snap-x snap-mandatory"
                  style="scrollbar-width: none; -ms-overflow-style: none"
                >
                  @for (slide of section.slides; track $index) {
                    <div class="relative group flex-shrink-0 snap-start">
                      <mtjr-blur-img
                        [src]="slide.image"
                        alt="{{ 'PRODUCTS.PRODUCT_IMAGE' | translate }}"
                        containerClass="h-[240px] w-[400px]"
                        imgClass="h-full w-full !rounded-2xl object-cover shadow-lg shadow-black/10 transition-all duration-500 group-hover:scale-[1.02] group-hover:shadow-xl"
                      />
                      <div
                        class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      ></div>
                    </div>
                  }
                </div>
              </div>
            }
            @case ("horizontalIconsOnly") {
              <div
                class="flex w-full gap-4 overflow-x-auto py-4 px-4 md:px-6 snap-x snap-mandatory"
                style="scrollbar-width: none; -ms-overflow-style: none"
              >
                @for (vendor of section.vendors; track $index) {
                  <div
                    [routerLink]="['vendors', vendor.id]"
                    routerDirection="forward"
                    class="group flex flex-col items-center gap-2 cursor-pointer flex-shrink-0 min-w-[90px] snap-start"
                  >
                    <div
                      class="relative p-1 rounded-full bg-gradient-to-br from-primary/20 via-primary/10 to-transparent transition-all duration-300 group-hover:from-primary/40 group-hover:via-primary/20 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-primary/20"
                    >
                      <mtjr-blur-img
                        proxy="https://dev-img.mtjr.ly/unsafe/resize:auto:200:200/sm:1/q:85/plain/"
                        [src]="vendor.logo"
                        [blurHash]="vendor.logoBlurHash"
                        alt="{{ 'VENDORS.LOGO_OF' | translate }} {{ vendor.name }}"
                        imgClass="rounded-full size-[72px] border-2 border-white dark:border-gray-800 shadow-md object-cover"
                        fetchpriority="high"
                      />
                    </div>
                    <ion-text
                      class="text-center text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary transition-colors duration-300 line-clamp-1 max-w-[80px]"
                    >
                      {{ vendor.name }}
                    </ion-text>
                  </div>
                }
              </div>
            }
            @case ("grid") {
              <div class="w-full px-4 md:px-6 py-2">
                <div
                  class="grid w-full grid-cols-2 xs:grid-cols-3 md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4"
                >
                  @for (product of section.products; track $index) {
                    <div class="transform transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1">
                      <mtjr-product-card
                        [product]="product"
                        class="shadow-md hover:shadow-xl transition-shadow duration-300 rounded-2xl overflow-hidden"
                      />
                    </div>
                  }
                </div>
              </div>
            }
            @case ("horizontal") {
              <div
                class="flex gap-4 overflow-x-auto py-3 px-4 md:px-6 snap-x snap-mandatory"
                style="scrollbar-width: none; -ms-overflow-style: none"
              >
                @for (product of section.products; track $index) {
                  <div
                    class="flex-shrink-0 snap-start transform transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1"
                  >
                    <mtjr-product-card
                      [product]="product"
                      class="min-w-[180px] shadow-md hover:shadow-xl transition-shadow duration-300 rounded-2xl overflow-hidden"
                    />
                  </div>
                }
              </div>
            }
            @case ("vertical") {
              <div class="w-full px-4 md:px-6 py-2">
                <div class="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
                  @for (product of section.products; track $index) {
                    <div class="transform transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1">
                      <mtjr-product-card
                        [product]="product"
                        class="shadow-md hover:shadow-xl transition-shadow duration-300 rounded-2xl overflow-hidden"
                      />
                    </div>
                  }
                </div>
              </div>
            }
          }
        </section>
      }
    </div>
  }
</ion-content>
